import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { CreateReportSidebarMode } from '../create-report-sidebar';
import { REPORT_FORM_FIELD_IDS } from '../constants';
import { CreationMethod } from '../screens/report-form-screen';

export const useCreateReportForm = (mode: CreateReportSidebarMode = 'report') => {
  const [reportName, setReportName] = useState('');
  const [creationMethod, setCreationMethod] = useState<CreationMethod>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [errors, setErrors] = useState<Record<string, { id: string; message: string }>>({});

  const intl = useIntl();
  const hasBeenValidated = useRef(false);
  const isDashboard = mode === 'dashboard';

  const validate = useCallback(() => {
    const newErrors: Record<string, { id: string; message: string }> = {};

    // Name
    if (!reportName.trim().length) {
      newErrors.name = {
        id: REPORT_FORM_FIELD_IDS.REPORT_NAME,
        message: isDashboard
          ? intl.formatMessage({
              id: 'reporting.validation.dashboard_name_is_required',
              defaultMessage: 'Dashboard name is required'
            })
          : intl.formatMessage({
              id: 'reporting.validation.report_name_is_required',
              defaultMessage: 'Report name is required'
            })
      };
    }

    // Creation method
    if (!creationMethod) {
      newErrors.creationMethod = {
        id: REPORT_FORM_FIELD_IDS.CREATION_METHOD,
        message: intl.formatMessage({
          id: 'reporting.validation.please_select_a_creation_method',
          defaultMessage: 'Please select a creation method'
        })
      };
    }

    // Template (only when a template-based method is selected)
    if (creationMethod === 'myTemplate' || creationMethod === 'futrliTemplate') {
      if (!selectedTemplateId) {
        newErrors.template = {
          id: creationMethod === 'myTemplate' ? REPORT_FORM_FIELD_IDS.WORKSPACE_TEMPLATE_SELECT : REPORT_FORM_FIELD_IDS.FUTRLI_TEMPLATE_SELECT,
          message: intl.formatMessage({
            id: 'reporting.validation.you_must_select_a_template',
            defaultMessage: 'You must select a template'
          })
        };
      }
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [reportName, creationMethod, selectedTemplateId, intl, isDashboard]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  // Auto-revalidate when creation method changes (clears the error immediately on selection)
  useEffect(() => {
    revalidate();
  }, [creationMethod, revalidate]);

  return {
    reportName,
    setReportName,
    creationMethod,
    setCreationMethod,
    selectedTemplateId,
    setSelectedTemplateId,
    errors,
    validate,
    revalidate
  };
};
